import {EndpointValue, FlowNodeTypes, SettingParam} from "@/types/node";
import {useCallback, useEffect, useRef, useState} from "react";
import {Tabs, TabsList, TabsTrigger, TabsContent} from "@/components/ui/tabs";
import {SettingComponentMap} from "@/components/settings/index";
import {Node} from "@xyflow/react";
import {FlowNodeData} from "@/types/node";
import {cn} from "@/lib/utils";
import {getSettingValueByKey} from "@/utils/index";
import {APIMethodEnum} from "@/api/models/common";

export const SettingTabs = ({
  param,
  handleChange,
  disabled = false,
  selectedNode,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: SettingParam["value"]) => void;
  disabled: boolean;
  selectedNode: Node<FlowNodeData>;
}) => {
  const [value, setValue] = useState(param.value);
  const [tabs, setTabs] = useState(param.tabs);
  const httpMethodRef = useRef<APIMethodEnum | null>(null);

  const [activeTab, setActiveTab] = useState(() => {
    const tabKey = Object.keys(value).find(
      tabKey => param.value?.[tabKey]?.selected,
    );
    return tabKey || param.tabs?.[0]?.key || "";
  });

  // Update tabs and active tab when HTTP method changes
  // TODO: Refactor this when we have dynamic blueprints.
  useEffect(() => {
    if (param.key === "body_and_variables") {
      const actionHttpMethod = getSettingValueByKey(
        selectedNode.data.settings,
        "endpoint",
      ) as EndpointValue;

      if (httpMethodRef.current !== actionHttpMethod.http_method) {
        httpMethodRef.current = actionHttpMethod.http_method as APIMethodEnum;

        const paramValue = param.value as Record<string, any>;
        Object.keys(paramValue).forEach(key => {
          paramValue[key].selected = false;
        });

        let filteredTabs = param.tabs;
        if (actionHttpMethod.http_method === APIMethodEnum.GET) {
          filteredTabs = param.tabs.filter(tab => tab.key !== "json");
          paramValue.query_parameters.selected = true;
        } else {
          filteredTabs = param.tabs.filter(
            tab => tab.key !== "query_parameters",
          );
          paramValue.json.selected = true;
        }

        setTabs(filteredTabs);

        const tabKey = Object.keys(param.value).find(
          tabKey => param.value?.[tabKey]?.selected,
        );
        if (tabKey) {
          setActiveTab(tabKey);
        }
      }
    }
  }, [selectedNode.data.settings, param.key, param.value, param.tabs]);

  const handleSettingChange = useCallback(
    (setting_param: SettingParam, setting_value: SettingParam["value"]) => {
      const transformedValue: Record<string, any> = {
        ...(value as Record<string, any>),
        [activeTab]: {
          ...(value?.[activeTab] || {}),
          [setting_param.key]: setting_value,
        },
      };

      setValue(transformedValue);
      handleChange(param, transformedValue);
    },
    [activeTab, value, handleChange, param],
  );

  const handleTabChange = useCallback(
    (selectedTab: string) => {
      Object.keys(param.value).forEach(key => {
        param.value[key].selected = false;
      });

      param.value[selectedTab] = {
        ...(param.value[selectedTab] || {}),
        selected: true,
      };

      setValue(param.value);
      handleChange(param, param.value);
      setActiveTab(selectedTab);
    },
    [value, handleChange, param],
  );

  return (
    <div className="mb-4 w-full">
      {param.label && (
        <label className="text-sm font-medium text-label mb-2 flex items-center justify-between">
          {param.label}
        </label>
      )}
      <Tabs className="w-full" value={activeTab}>
        <TabsList>
          {tabs.map(tab => (
            <TabsTrigger
              value={tab.key}
              key={tab.key}
              disabled={disabled}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.title}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map(
          tab =>
            tab.settings.length > 0 && (
              <TabsContent value={tab.key} key={tab.key}>
                {tab.settings.map(setting => {
                  const SettingComponent =
                    SettingComponentMap[
                      setting.type as keyof typeof SettingComponentMap
                    ];

                  const param = {
                    ...setting,
                    value: value?.[tab.key]?.[setting.key] || "",
                  };

                  return SettingComponent ? (
                    <SettingComponent
                      key={setting.key}
                      disabled={disabled}
                      param={param}
                      handleChange={handleSettingChange}
                      placeholder={param.placeholder}
                      className={param.className}
                    />
                  ) : null;
                })}
              </TabsContent>
            ),
        )}
      </Tabs>
    </div>
  );
};
